import { useId, useMemo, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import type { ProgramView } from '../programs/types';
import {
  PROMPT_LEVELS,
  createSession,
  deleteSession,
  editSeries,
  updateSession,
  type Activity,
  type Goal,
  type ItemInput,
  type ItemKind,
  type Scope,
  type Session,
  type SessionInput,
} from './api';
import { parseRepeat, shortTime, useDates, weekday } from './dates';
import PlannerDialog, { ScopeDialog } from './PlannerDialog';
import './extras.css';
import { sessionRepeat, useRepeatText } from './SessionParts';
import DateField from '../components/DateField';

type Draft = {
  key: string;
  kind: ItemKind;
  programId: string | null;
  activityId: string | null;
  text: string;
  label: string;
  targetStatement: string | null;
  trials: boolean;
  targetTrials: string;
  minutes: string;
  materials: string;
  promptStart: string;
  reward: string;
};

let seq = 0;
const nextKey = () => `d${++seq}`;
const num = (s: string) => (s.trim() === '' || Number.isNaN(Number(s)) ? null : Math.max(0, Math.round(Number(s))));
const str = (s: string) => (s.trim() === '' ? null : s.trim());

export function activityIndex(goals: Goal[]): Map<string, { activity: Activity; target: string; goal: string }> {
  const map = new Map<string, { activity: Activity; target: string; goal: string }>();
  for (const g of goals) for (const tg of g.targets) for (const a of tg.activities) map.set(a.id, { activity: a, target: tg.statement, goal: g.statement });
  return map;
}

export function sessionToInput(s: Session): SessionInput {
  return {
    day: s.day,
    startTime: shortTime(s.startTime),
    minutes: s.minutes,
    who: s.who,
    title: s.title,
    note: s.note,
    status: s.status,
    items: s.items.map((i) => ({
      kind: i.kind, programId: i.programId, activityId: i.activityId, text: i.text, targetTrials: i.targetTrials,
      minutes: i.minutes, materials: i.materials, promptStart: i.promptStart, reward: i.reward,
    })),
  };
}

const trim = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

type Props = {
  learnerId: string;
  session: Session | null;
  day: string;
  goals: Goal[];
  programs: ProgramView[];
  onClose: () => void;
  onSaved: () => void;
};

export default function SessionEditor({ learnerId, session, day: startDay, goals, programs, onClose, onSaved }: Props) {
  const t = useT();
  const id = useId();
  const repeatText = useRepeatText();
  const activities = useMemo(() => activityIndex(goals), [goals]);

  const [day, setDay] = useState(session?.day ?? startDay);
  const [startTime, setStartTime] = useState(shortTime(session?.startTime ?? null) ?? '');
  const [minutes, setMinutes] = useState(session?.minutes != null ? String(session.minutes) : '');
  const [who, setWho] = useState(session?.who ?? '');
  const [title, setTitle] = useState(session?.title ?? '');
  const [items, setItems] = useState<Draft[]>(() => (session?.items ?? []).map((i) => {
    const a = i.activityId ? activities.get(i.activityId) : undefined;
    return {
      key: nextKey(),
      kind: i.kind,
      programId: i.programId,
      activityId: i.activityId,
      text: i.text ?? '',
      label: i.programName ?? i.activityTitle ?? '',
      targetStatement: i.targetStatement,
      trials: i.kind === 'PROGRAM' || a?.activity.measure === 'TRIALS' || a?.activity.kind === 'PROGRAM',
      targetTrials: i.targetTrials != null ? String(i.targetTrials) : '',
      minutes: i.minutes != null ? String(i.minutes) : '',
      materials: i.materials ?? '',
      promptStart: i.promptStart ?? '',
      reward: i.reward ?? '',
    };
  }));
  const [repeats, setRepeats] = useState(false);
  const [repeatDays, setRepeatDays] = useState<number[]>([]);
  const [until, setUntil] = useState('');
  const [pickProgram, setPickProgram] = useState('');
  const [pickActivity, setPickActivity] = useState('');
  const [freeText, setFreeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ask, setAsk] = useState<'save' | 'delete' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const parsedRepeat = session ? parseRepeat(session.repeat) : null;
  const [seriesDays, setSeriesDays] = useState<number[]>(parsedRepeat?.days ?? (session ? [weekday(session.day)] : []));
  const [seriesUntil, setSeriesUntil] = useState(parsedRepeat?.until ?? '');
  const [seriesBusy, setSeriesBusy] = useState(false);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [seriesDone, setSeriesDone] = useState<string | null>(null);

  const blank = { minutes: '', materials: '', promptStart: '', reward: '', targetTrials: '', text: '', targetStatement: null };

  function addProgram() {
    const p = programs.find((x) => x.id === pickProgram);
    if (!p) return;
    setItems((l) => [...l, { ...blank, key: nextKey(), kind: 'PROGRAM', programId: p.id, activityId: null, label: p.name, trials: true, targetTrials: '10' }]);
    setPickProgram('');
  }

  function addActivity() {
    const a = activities.get(pickActivity);
    if (!a) return;
    const trials = a.activity.measure === 'TRIALS' || a.activity.kind === 'PROGRAM';
    setItems((l) => [...l, { ...blank, key: nextKey(), kind: 'ACTIVITY', programId: null, activityId: a.activity.id, label: a.activity.title, targetStatement: a.target, trials, targetTrials: trials ? '10' : '' }]);
    setPickActivity('');
  }

  function addText() {
    if (!freeText.trim()) return;
    setItems((l) => [...l, { ...blank, key: nextKey(), kind: 'TEXT', programId: null, activityId: null, text: freeText.trim(), label: '', trials: false }]);
    setFreeText('');
  }

  const patch = (key: string, p: Partial<Draft>) => setItems((l) => l.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const shift = (at: number, by: number) => setItems((l) => {
    const to = at + by;
    if (to < 0 || to >= l.length) return l;
    const copy = [...l];
    [copy[at], copy[to]] = [copy[to], copy[at]];
    return copy;
  });

  function build(): SessionInput | null {
    if (!day) { setError(t('Choose a date.')); return null; }
    if (items.length === 0 && !title.trim()) { setError(t('Add a title or at least one thing to do.')); return null; }
    if (items.some((i) => i.kind === 'TEXT' && !i.text.trim())) { setError(t('Write what each "something else" item is, or remove it.')); return null; }
    if (!session && repeats) {
      if (repeatDays.length === 0) { setError(t('Choose at least one day for the repeats.')); return null; }
      if (!until) { setError(t('Choose when the repeats end.')); return null; }
      if (until < day) { setError(t('The repeats must end on or after the first date.')); return null; }
    }
    const list: ItemInput[] = items.map((i) => ({
      kind: i.kind,
      programId: i.programId,
      activityId: i.activityId,
      text: i.kind === 'TEXT' ? i.text.trim() : null,
      targetTrials: i.trials ? num(i.targetTrials) : null,
      minutes: num(i.minutes),
      materials: str(i.materials),
      promptStart: str(i.promptStart),
      reward: str(i.reward),
    }));
    return {
      day,
      startTime: startTime || null,
      minutes: num(minutes),
      who: str(who),
      title: str(title),
      note: session?.note ?? null,
      status: session?.status ?? 'PLANNED',
      items: list,
      ...(!session && repeats ? { repeatDays: [...repeatDays].sort((a, b) => a - b), repeatUntil: until } : {}),
    };
  }

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  function save(scope?: Scope) {
    const body = build();
    if (!body) return;
    if (!session) { void run(() => createSession(learnerId, body)); return; }
    if (session.seriesId && !scope) { setAsk('save'); return; }
    void run(() => updateSession(learnerId, session.id, body, scope ?? 'ONE'));
  }

  function remove(scope?: Scope) {
    if (!session) return;
    if (session.seriesId && !scope) { setAsk('delete'); return; }
    if (!session.seriesId && !scope) { setConfirmDelete(true); return; }
    void run(() => deleteSession(learnerId, session.id, scope ?? 'ONE'));
  }

  const existingRepeat = session ? sessionRepeat(session, repeatText) : null;

  async function changeSeries() {
    if (!session?.seriesId) return;
    if (seriesDays.length === 0) { setSeriesError(t('Choose at least one day for the repeats.')); return; }
    if (!seriesUntil) { setSeriesError(t('Choose when the repeats end.')); return; }
    if (seriesUntil < session.day) { setSeriesError(t('The repeats must end on or after the first date.')); return; }
    setSeriesBusy(true);
    setSeriesError(null);
    try {
      const r = await editSeries(learnerId, session.seriesId, session.id, [...seriesDays].sort((a, b) => a - b), seriesUntil);
      setSeriesDone(t('{count} sessions added, {kept} kept', { count: r.count, kept: r.kept }));
      onSaved();
    } catch (e) {
      setSeriesError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setSeriesBusy(false);
    }
  }

  return (
    <>
      <PlannerDialog title={session ? t('Change the session') : t('Add a session')} onClose={onClose} className="planner-session-dialog">
        <form onSubmit={(e) => { e.preventDefault(); save(); }}>
          <div className="row">
            <div className="field">
              <label htmlFor={`${id}-day`}>{session?.seriesId ? t('Date of this session') : t('Date')}</label>
              <DateField id={`${id}-day`} required value={day} onChange={(v) => setDay(v ?? '')} />
            </div>
            <div className="field">
              <label htmlFor={`${id}-time`}>{t('Start time (optional)')}</label>
              <input id={`${id}-time`} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${id}-mins`}>{t('Minutes')}</label>
              <input id={`${id}-mins`} type="number" min={0} max={600} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor={`${id}-who`}>{t('Who runs it')}</label>
              <input id={`${id}-who`} maxLength={120} value={who} onChange={(e) => setWho(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${id}-title`}>{t('Title (optional)')}</label>
              <input id={`${id}-title`} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
              <span className="field-hint">{t('If you leave this empty, the session is named after what is in it.')}</span>
            </div>
          </div>

          <fieldset className="planner-fieldset">
            <legend>{t('Repeat')}</legend>
            {session ? (
              <>
                <p>{existingRepeat ?? t('Does not repeat')}</p>
                {session.seriesId && (
                  <div className="planner-tpl-series">
                    <h3 className="planner-tpl-heading">{t('Change repeat days')}</h3>
                    <p className="field-hint">{t('Sessions from this one onwards are remade on the new days. Sessions that already have results, notes or comments are kept.')}</p>
                    <div className="planner-weekdays" role="group" aria-label={t('Days')}>
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                        const on = seriesDays.includes(n);
                        return (
                          <WeekdayToggle key={n} n={n} on={on} onToggle={() => setSeriesDays((l) => (on ? l.filter((x) => x !== n) : [...l, n]))} />
                        );
                      })}
                    </div>
                    <div className="field">
                      <label htmlFor={`${id}-series-until`}>{t('Until')}</label>
                      <DateField id={`${id}-series-until`} min={session.day} value={seriesUntil} onChange={(v) => setSeriesUntil(v ?? '')} />
                    </div>
                    <p className="planner-repeat-sentence" aria-live="polite">{repeatText(seriesDays, seriesUntil || null)}</p>
                    {seriesError && <p className="error" role="alert">{seriesError}</p>}
                    {seriesDone && <p className="planner-tpl-done" role="status">{seriesDone}</p>}
                    <div className="toolbar">
                      <button type="button" className="btn secondary" disabled={seriesBusy || seriesDone !== null} onClick={() => void changeSeries()}>{t('Change repeat days')}</button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="planner-radios">
                  <label><input type="radio" name={`${id}-rep`} checked={!repeats} onChange={() => setRepeats(false)} /> {t('Does not repeat')}</label>
                  <label>
                    <input
                      type="radio"
                      name={`${id}-rep`}
                      checked={repeats}
                      onChange={() => { setRepeats(true); if (repeatDays.length === 0 && day) setRepeatDays([weekday(day)]); }}
                    /> {t('Repeats every week')}
                  </label>
                </div>
                {repeats && (
                  <>
                    <div className="planner-weekdays" role="group" aria-label={t('Days')}>
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                        const on = repeatDays.includes(n);
                        return (
                          <WeekdayToggle key={n} n={n} on={on} onToggle={() => setRepeatDays((l) => (on ? l.filter((x) => x !== n) : [...l, n]))} />
                        );
                      })}
                    </div>
                    <div className="field">
                      <label htmlFor={`${id}-until`}>{t('Until')}</label>
                      <DateField id={`${id}-until`} min={day} value={until} onChange={(v) => setUntil(v ?? '')} />
                    </div>
                    <p className="planner-repeat-sentence" aria-live="polite">{repeatText(repeatDays, until || null)}</p>
                  </>
                )}
              </>
            )}
          </fieldset>

          <h3>{t('What to do, in order')}</h3>
          {items.length === 0 && <p className="muted">{t('Nothing added yet.')}</p>}
          <ol className="planner-edit-items">
            {items.map((i, at) => {
              const name = i.kind === 'TEXT' ? i.text || t('Something else') : i.label;
              return (
                <li key={i.key} className="planner-edit-item">
                  <div className="planner-edit-item-head">
                    <span className="pill">{i.kind === 'PROGRAM' ? t('EasyLearning program') : i.kind === 'ACTIVITY' ? t('Activity') : t('Something else')}</span>
                    {i.kind !== 'TEXT' && <strong>{i.label}</strong>}
                  </div>
                  {i.targetStatement && <p className="planner-meta">{t('Target: {target}', { target: i.targetStatement })}</p>}
                  {i.kind === 'TEXT' && (
                    <div className="field">
                      <label htmlFor={`${id}-${i.key}-text`}>{t('What is it?')}</label>
                      <input id={`${id}-${i.key}-text`} maxLength={500} value={i.text} onChange={(e) => patch(i.key, { text: e.target.value })} />
                    </div>
                  )}
                  <div className="row">
                    {i.trials && (
                      <div className="field">
                        <label htmlFor={`${id}-${i.key}-tt`}>{t('Target answers (optional)')}</label>
                        <input id={`${id}-${i.key}-tt`} type="number" min={0} max={999} value={i.targetTrials} onChange={(e) => patch(i.key, { targetTrials: e.target.value })} />
                      </div>
                    )}
                    <div className="field">
                      <label htmlFor={`${id}-${i.key}-min`}>{t('Minutes')}</label>
                      <input id={`${id}-${i.key}-min`} type="number" min={0} max={600} value={i.minutes} onChange={(e) => patch(i.key, { minutes: e.target.value })} />
                    </div>
                    <div className="field">
                      <label htmlFor={`${id}-${i.key}-ps`}>{t('Starting help')}</label>
                      <select id={`${id}-${i.key}-ps`} value={i.promptStart} onChange={(e) => patch(i.key, { promptStart: e.target.value })}>
                        <option value="">{t('Not set')}</option>
                        {PROMPT_LEVELS.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="field">
                      <label htmlFor={`${id}-${i.key}-mat`}>{t('Materials')}</label>
                      <input id={`${id}-${i.key}-mat`} maxLength={500} value={i.materials} onChange={(e) => patch(i.key, { materials: e.target.value })} />
                    </div>
                    <div className="field">
                      <label htmlFor={`${id}-${i.key}-rew`}>{t('Reward')}</label>
                      <input id={`${id}-${i.key}-rew`} maxLength={200} value={i.reward} onChange={(e) => patch(i.key, { reward: e.target.value })} />
                    </div>
                  </div>
                  <div className="toolbar">
                    <button type="button" className="btn secondary" disabled={at === 0} onClick={() => shift(at, -1)} aria-label={t('Move {name} up', { name })}>{t('Up')}</button>
                    <button type="button" className="btn secondary" disabled={at === items.length - 1} onClick={() => shift(at, 1)} aria-label={t('Move {name} down', { name })}>{t('Down')}</button>
                    <button type="button" className="btn quiet-danger" onClick={() => setItems((l) => l.filter((x) => x.key !== i.key))} aria-label={t('Remove {name}', { name })}>{t('Remove')}</button>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="planner-add-items">
            <h3>{t('Add to this session')}</h3>
            <div className="planner-add-row">
              <div className="field">
                <label htmlFor={`${id}-prog`}>{t('An EasyLearning program')}</label>
                <select id={`${id}-prog`} value={pickProgram} onChange={(e) => setPickProgram(e.target.value)}>
                  <option value="">{programs.length === 0 ? t('No programs yet') : t('Choose a program')}</option>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <button type="button" className="btn secondary" disabled={!pickProgram} onClick={addProgram}>{t('Add program')}</button>
            </div>
            <div className="planner-add-row">
              <div className="field">
                <label htmlFor={`${id}-act`}>{t('An activity from a target')}</label>
                <select id={`${id}-act`} value={pickActivity} onChange={(e) => setPickActivity(e.target.value)}>
                  <option value="">{activities.size === 0 ? t('No activities yet. Add them under Goals and targets.') : t('Choose an activity')}</option>
                  {goals.map((g) => g.targets.filter((tg) => tg.activities.length > 0).map((tg) => (
                    <optgroup key={tg.id} label={`${trim(g.statement, 50)} → ${trim(tg.statement, 70)}`}>
                      {tg.activities.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                    </optgroup>
                  )))}
                </select>
              </div>
              <button type="button" className="btn secondary" disabled={!pickActivity} onClick={addActivity}>{t('Add activity')}</button>
            </div>
            <div className="planner-add-row">
              <div className="field">
                <label htmlFor={`${id}-free`}>{t('Something else')}</label>
                <input
                  id={`${id}-free`}
                  maxLength={500}
                  value={freeText}
                  placeholder={t('Visit the park, practise waiting at the crossing')}
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addText(); } }}
                />
              </div>
              <button type="button" className="btn secondary" disabled={!freeText.trim()} onClick={addText}>{t('Add')}</button>
            </div>
          </div>

          {error && <div className="error" role="alert">{error}</div>}

          <div className="form-actions">
            <button type="submit" className="btn" disabled={busy || seriesDone !== null}>{session ? t('Save changes') : t('Add session')}</button>
            <button type="button" className="btn secondary" onClick={onClose}>{t('Cancel')}</button>
            {session && <button type="button" className="btn quiet-danger" disabled={busy} onClick={() => remove()}>{t('Delete session')}</button>}
          </div>
        </form>
      </PlannerDialog>
      {ask && (
        <ScopeDialog
          title={ask === 'save' ? t('Which sessions should change?') : t('Which sessions should be deleted?')}
          destructive={ask === 'delete'}
          onCancel={() => setAsk(null)}
          onChoose={(scope) => {
            const what = ask;
            setAsk(null);
            if (what === 'save') save(scope);
            else remove(scope);
          }}
        />
      )}
      <ConfirmDialog
        open={confirmDelete}
        title={t('Delete this session?')}
        message={t('The session, its results and its comments are removed. This cannot be undone.')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Keep')}
        destructive
        onConfirm={() => { setConfirmDelete(false); remove('ONE'); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

function WeekdayToggle({ n, on, onToggle }: { n: number; on: boolean; onToggle: () => void }) {
  const d = useDates();
  return (
    <button type="button" className={`btn ${on ? '' : 'secondary'} planner-weekday`} aria-pressed={on} onClick={onToggle}>
      {on && <span aria-hidden="true">✓ </span>}
      <abbr title={d.weekdayLong(n)}>{d.weekdayShort(n)}</abbr>
    </button>
  );
}
